import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useStatus } from '../../contexts/StatusContext';
import { getHistory, clearHistory } from '../../utils/history';
import { classifyStatus } from '../../utils/checker';
import { normalizeInput } from '../../utils/urlUtils';
import { checkStatus } from '../../utils/checker';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiRefreshCw, FiTrash2, FiCheckCircle, FiAlertTriangle, FiXCircle } = FiIcons;

const HistoryTab = () => {
  const { setCurrentResult, setIsChecking, settings } = useStatus();
  const history = getHistory();

  // Helper function to get domain from URL
  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  };

  const handleRecheck = async (item) => {
    setIsChecking(true);
    try {
      const normalizedUrl = normalizeInput(item.input, settings.protocol);
      const result = await checkStatus(normalizedUrl, settings);
      setCurrentResult({
        ...result,
        input: item.input,
        normalizedUrl,
        timestamp: new Date().toISOString(),
        id: Date.now().toString()
      });
    } catch (err) {
      console.error('Recheck failed:', err);
    } finally {
      setIsChecking(false);
    }
  };

  const handleClearHistory = () => {
    if (confirm('Are you sure you want to clear all history? This action cannot be undone.')) {
      clearHistory();
      window.location.reload(); // Simple way to refresh the component
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Up': return FiCheckCircle;
      case 'Degraded': return FiAlertTriangle;
      case 'Down': return FiXCircle;
      default: return FiXCircle;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Up': return 'text-green-600 dark:text-green-400';
      case 'Degraded': return 'text-yellow-600 dark:text-yellow-400';
      case 'Down': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (history.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 text-gray-500 dark:text-gray-400">
        <SafeIcon icon={FiClock} className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base sm:text-lg font-medium mb-2">No history yet</p>
        <p className="text-xs sm:text-sm">Your last 500 checks will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          Recent Checks ({history.length}/500)
        </h3>
        <motion.button
          onClick={handleClearHistory}
          className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors focus-ring"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
          <span>Clear History</span>
        </motion.button>
      </div>

      <div className="space-y-3">
        {history.map((item, index) => {
          const status = classifyStatus(item);
          const StatusIcon = getStatusIcon(status);
          const statusColor = getStatusColor(status);
          const domain = getDomainFromUrl(item.finalUrl || item.normalizedUrl);

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {domain && (
                      <img 
                        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
                        alt={`${domain} favicon`}
                        className="w-4 h-4 rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <SafeIcon icon={StatusIcon} className={`h-4 sm:h-5 w-4 sm:w-5 flex-shrink-0 ${statusColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate text-sm sm:text-base">
                      {item.input}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      {new Date(item.timestamp).toLocaleString()} • {item.responseTimeMs}ms
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between sm:justify-end space-x-4">
                  <div className="flex items-center space-x-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor} bg-white dark:bg-gray-800`}>
                      {status}
                    </span>
                    <span>HTTP {item.httpCode || 'N/A'}</span>
                  </div>
                  
                  <motion.button
                    onClick={() => handleRecheck(item)}
                    className="p-2 text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors focus-ring flex-shrink-0"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Recheck this URL"
                  >
                    <SafeIcon icon={FiRefreshCw} className="h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HistoryTab;